import { ReactElement, SetStateAction } from 'react';
import { ITimesheetsJobToInvoice } from '../../types';
import dayjs from '@jobcost-monorepo/dayjs-config';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  cn,
  floatToHours,
  isHoliday,
  isWeekend,
  ITimesheetsReportGlobalPrint,
  KeyValue,
} from '@jobcost-monorepo/utils';
import IconSVG from '@jobcost-monorepo/ui/atoms/Icons';
import { Checkbox } from '@jobcost-monorepo/ui';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { MarkType } from '@jobcost-monorepo/database/prisma/enums';

interface IProps {
  data: ITimesheetsReportGlobalPrint;
  groupedData: ITimesheetsReportGlobalPrint[];
  startDate: string;
  endDate: string;
  setGroupedData: React.Dispatch<
    React.SetStateAction<ITimesheetsReportGlobalPrint[]>
  >;
  setInvoiceFormOpen: (
    value: SetStateAction<ITimesheetsJobToInvoice | undefined>,
  ) => void;
}

const TimesheetReportGlobalBodyRow = ({
  data,
  groupedData,
  setGroupedData,
  startDate,
  endDate,
  setInvoiceFormOpen,
}: IProps): ReactElement => {
  const { t } = useTranslation(['timesheets', 'commons']);
  const totalShortLabel = t('timesheetReportGlobal.totalShort');
  const start = dayjs.utc(startDate);
  const end = dayjs.utc(endDate);
  const dates: Dayjs[] = [];
  let currentDate = start;

  while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
    dates.push(currentDate);
    currentDate = currentDate.add(1, 'day');
  }

  const getContentForDateAndJobOrMarkType = (
    jobOrMarkType: KeyValue | MarkType,
    date: Dayjs,
  ) => {
    const timesheetDate = data.data.groupedDate.find((el) =>
      dayjs(el.operationDate).isSame(date, 'day'),
    );
    if (!timesheetDate) return <div></div>;

    const totals = timesheetDate.totalsByJobOrMarkType.find((el) =>
      typeof el.jobOrMarkType === 'object' && typeof jobOrMarkType === 'object'
        ? el.jobOrMarkType.id === jobOrMarkType.id
        : el.jobOrMarkType === jobOrMarkType,
    );

    return (
      <div
        className={cn(
          (isWeekend(date.toDate()) || isHoliday(date.toDate())) &&
            'text-red font-bold',
        )}
      >
        {floatToHours(totals?.totalHours)}
      </div>
    );
  };

  const handleSelectPrint = () => {
    setGroupedData([
      ...groupedData.map((singleData) => {
        return singleData.data.resource.id !== data.data.resource.id
          ? singleData
          : { data: data.data, print: !data.print };
      }),
    ]);
  };

  return (
    <div>
      <div key={data.data.resource.id} className="mb-4">
        <table
          key={`timesheetReportGlobalBodyRowDetails-${data.data.resource.id}`}
          className="table-auto overflow-auto border-[1px] border-gray-400 w-full mb-4 rounded-lg"
        >
          <thead>
            <tr className="bg-gray-100">
              <th className="min-w-52 p-2 border-[1px] border-gray-300 rounded-tl-lg text-left">
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-row items-center">
                    <div>{data.data.resource.name}</div>
                    {!!data.data.lockedId && (
                      <LockClosedIcon className="h-5 w-5 ml-1" />
                    )}
                  </div>

                  <Checkbox checked={data.print} onClick={handleSelectPrint} />
                </div>
              </th>
              {dates.map((date) => (
                <th
                  key={date.toISOString()}
                  className={cn(
                    'w-fit text-xs text-center border-[1px] border-gray-300',
                    (isWeekend(date.toDate()) || isHoliday(date.toDate())) &&
                      'bg-gray dark:bg-cyan-900',
                  )}
                >
                  <div>{date.locale('it').format('ddd').toUpperCase()}</div>
                  <div>{date.format('DD')}</div>
                </th>
              ))}
              <th className="w-20 min-w-7 text-xs text-center border-[1px] border-gray-300 rounded-tr-lg">
                {totalShortLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.data.totalsByJobOrMarkType.map((el, index) => {
              return (
                <tr
                  key={`${data.data.resource.name}-${index}`}
                  className={
                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white dark:bg-dark'
                  }
                >
                  <td className="min-w-52 pl-1 border-[1px] border-gray-300">
                    <div className="flex flex-row w-full justify-between">
                      <div>
                        {typeof el.jobOrMarkType === 'object'
                          ? el.jobOrMarkType.name
                          : t(`enum.markType.${el.jobOrMarkType}`)}
                      </div>
                      {typeof el.jobOrMarkType === 'object' &&
                        el.jobOrMarkType.id !== 0 && (
                          <div
                            onClick={() =>
                              setInvoiceFormOpen({
                                // @ts-ignore
                                jobId: el.jobOrMarkType.id,
                                resourceId: data.data.resource.id,
                                totalHours: el.totalHours,
                              })
                            }
                          >
                            <IconSVG
                              iconName="payment"
                              className="hover:opacity-50 hover:cursor-pointer fill-black stroke-black dark:fill-white dark:stroke-white no-print mr-1"
                            />
                          </div>
                        )}
                    </div>
                  </td>
                  {dates.map((date) => (
                    <td
                      key={`${data.data.resource.name}-${index}-${date.toISOString()}`}
                      className={cn(
                        'w-fit text-xs text-center border-[1px] border-gray-300',
                        (isWeekend(date.toDate()) ||
                          isHoliday(date.toDate())) &&
                          'bg-gray dark:bg-cyan-900',
                      )}
                    >
                      {getContentForDateAndJobOrMarkType(
                        el.jobOrMarkType,
                        date,
                      )}
                    </td>
                  ))}
                  <td className="text-center border-[1px] border-gray-300 font-bold min-w-20">
                    {floatToHours(el.totalHours)}{' '}
                    {typeof el.jobOrMarkType !== 'object' &&
                      t(`enum.markTypeShort.${el.jobOrMarkType}`).toUpperCase()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TimesheetReportGlobalBodyRow;
